import { useEffect, useId, useMemo, useState } from 'react';
import type { DiagramDocument } from '@ukrop-nm/shared';
import {
  buildPurposeText,
  type DrawingSheetMeta,
} from '../export/drawingMeta';

const FIELD =
  'focus-ring h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200';

const AREA =
  'focus-ring min-h-11 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200';

const BTN =
  'inline-flex h-11 items-center justify-center rounded-xl px-4 text-sm font-medium transition';

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  const id = useId();
  return (
    <label className="grid gap-1 text-sm">
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <input
        id={id}
        className={FIELD}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function AreaField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const id = useId();
  return (
    <label className="grid gap-1 text-sm">
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <textarea
        id={id}
        className={AREA}
        value={value}
        placeholder={placeholder}
        rows={3}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export function DrawingMetaModal({
  open,
  initial,
  diagram,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  initial: DrawingSheetMeta;
  diagram: DiagramDocument;
  onCancel: () => void;
  onConfirm: (meta: DrawingSheetMeta) => void;
}) {
  const [draft, setDraft] = useState(initial);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  const purposePreview = useMemo(() => buildPurposeText(diagram, draft), [diagram, draft]);

  if (!open) return null;

  const set =
    (key: keyof DrawingSheetMeta) =>
    (value: string) =>
      setDraft((prev) => ({ ...prev, [key]: value }));

  return (
    <div
      className="modal-root"
      role="presentation"
      onClick={onCancel}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
    >
      <div className="modal-backdrop" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="modal-panel modal-panel-wide"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id={titleId}>Реквізити креслення мережі</h3>
        </div>
        <div className="modal-body grid gap-3">
          <p id={descId} className="text-sm text-gray-500">
            Заповніть реквізити та відповіді для розділу «Призначення». На креслення потрапляють
            усі елементи схеми, включно із закріпленими (замок лише фіксує положення).
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Організація" value={draft.organization} onChange={set('organization')} />
            <Field label="Позначення" value={draft.sheetCode} onChange={set('sheetCode')} />
            <Field label="Проєкт" value={draft.projectName} onChange={set('projectName')} />
            <Field label="Обʼєкт / майданчик" value={draft.objectName} onChange={set('objectName')} />
            <div className="sm:col-span-2">
              <Field
                label="Найменування креслення"
                value={draft.drawingTitle}
                onChange={set('drawingTitle')}
              />
            </div>
            <Field label="Розробив" value={draft.author} onChange={set('author')} />
            <Field label="Перевірив" value={draft.checkedBy} onChange={set('checkedBy')} />
            <Field label="Дата" value={draft.date} onChange={set('date')} type="date" />
            <Field label="Масштаб" value={draft.scale} onChange={set('scale')} />
            <Field label="Ревізія" value={draft.revision} onChange={set('revision')} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Лист" value={draft.sheet} onChange={set('sheet')} />
              <Field label="Аркушів" value={draft.sheetsTotal} onChange={set('sheetsTotal')} />
            </div>
            <div className="sm:col-span-2">
              <AreaField
                label="Мета документа (для «Призначення»)"
                value={draft.purposeGoal}
                onChange={set('purposeGoal')}
                placeholder="Наприклад: документація LAN/WAN перед впровадженням моніторингу"
              />
            </div>
            <div className="sm:col-span-2">
              <AreaField
                label="Для кого документ"
                value={draft.purposeAudience}
                onChange={set('purposeAudience')}
                placeholder="Наприклад: адміністратори мережі ПУ, підрядник звʼязку"
              />
            </div>
            <div className="sm:col-span-2">
              <AreaField
                label="Межі схеми (що входить / не входить)"
                value={draft.purposeScope}
                onChange={set('purposeScope')}
                placeholder="Наприклад: логічна схема зовнішніх каналів і ядра; без фізичних трас СКС"
              />
            </div>
            <div className="sm:col-span-2">
              <Field
                label="Примітка до креслення"
                value={draft.notes}
                onChange={set('notes')}
                placeholder="Додаткова примітка в штампі та описі"
              />
            </div>
            <div className="sm:col-span-2 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/60">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Попередній перегляд «Призначення»
              </div>
              <p className="text-sm leading-5 text-gray-700 dark:text-gray-300">{purposePreview}</p>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className={`${BTN} border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200`}
            onClick={onCancel}
          >
            Скасувати
          </button>
          <button
            type="button"
            className={`${BTN} bg-brand-500 text-white hover:bg-brand-600`}
            onClick={() => onConfirm(draft)}
          >
            Створити креслення
          </button>
        </div>
      </div>
    </div>
  );
}
